import type { LexicalEditor } from "lexical";

import {
  NetworkError,
  PrepareShareError,
  prepareShare,
  type MediaRef,
} from "../../lib/api-client.js";
import type { TelegramWebApp } from "../../lib/telegram-types.js";

/**
 * Discriminated outcome of a share attempt. Maps to a single user-facing
 * toast / modal in the UI layer.
 */
export type ShareOutcome =
  | { kind: "success" }
  | { kind: "cancelled" }
  | {
      kind: "backend_error";
      code: string;
      extra: Record<string, unknown>;
    }
  | { kind: "network_error" }
  | { kind: "unsupported" }
  | { kind: "unknown_error"; message: string };

export interface ShareControllerDeps {
  /**
   * Override the call to Telegram's native shareMessage. Defaults to
   * `wa.shareMessage` when present. Returns `true` if the message was
   * actually sent, `false` if the user cancelled or the dialog failed.
   */
  shareMessage?: (
    wa: TelegramWebApp,
    preparedMessageId: string,
  ) => Promise<boolean>;
  /** Override `prepareShare` for tests. */
  prepareShareFn?: typeof prepareShare;
}

/** Returns true if this Telegram client supports `shareMessage`. */
export function isShareMessageSupported(wa: TelegramWebApp | null): boolean {
  if (!wa) return false;
  if (typeof wa.shareMessage !== "function") return false;
  // shareMessage requires Bot API 8.0; isVersionAtLeast is itself only
  // available on newer clients, so guard the call too.
  if (typeof wa.isVersionAtLeast === "function") {
    return wa.isVersionAtLeast("8.0");
  }
  return true;
}

function defaultShareMessage(
  wa: TelegramWebApp,
  preparedMessageId: string,
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    if (typeof wa.shareMessage !== "function") {
      reject(new Error("shareMessage not available"));
      return;
    }
    try {
      wa.shareMessage(preparedMessageId, (success, error) => {
        if (error) {
          reject(new Error(error));
          return;
        }
        resolve(success);
      });
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

/**
 * Run the full share-to-Telegram flow:
 *
 * 1. serialize the editor's current state
 * 2. POST to the backend's /api/prepare-share
 * 3. invoke `WebApp.shareMessage(prepared_id)` to open the native sheet
 *
 * All branches resolve to a `ShareOutcome` — the function never throws,
 * so the UI layer can always show a single toast/modal.
 */
export async function shareCurrentDraft(opts: {
  editor: LexicalEditor;
  webApp: TelegramWebApp | null;
  media?: MediaRef;
  deps?: ShareControllerDeps;
}): Promise<ShareOutcome> {
  const { editor, webApp, media } = opts;
  const deps = opts.deps ?? {};

  if (!webApp) {
    return { kind: "unsupported" };
  }
  if (!isShareMessageSupported(webApp)) {
    return { kind: "unsupported" };
  }

  const initData = webApp.initData;
  if (!initData) {
    return { kind: "backend_error", code: "auth_required", extra: {} };
  }

  const state = editor.getEditorState().toJSON();

  let preparedId: string;
  try {
    const fn = deps.prepareShareFn ?? prepareShare;
    const res = await fn({
      state,
      initData,
      ...(media ? { media } : {}),
    });
    preparedId = res.prepared_message_id;
  } catch (err) {
    if (err instanceof PrepareShareError) {
      return { kind: "backend_error", code: err.code, extra: err.extra };
    }
    if (err instanceof NetworkError) {
      return { kind: "network_error" };
    }
    return {
      kind: "unknown_error",
      message: err instanceof Error ? err.message : String(err),
    };
  }

  try {
    const fn = deps.shareMessage ?? defaultShareMessage;
    const sent = await fn(webApp, preparedId);
    return sent ? { kind: "success" } : { kind: "cancelled" };
  } catch (err) {
    return {
      kind: "unknown_error",
      message: err instanceof Error ? err.message : String(err),
    };
  }
}
